import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, Loader2 } from "lucide-react";
import { useAllModules, usePackageModules, useAddPackageModule, useRemovePackageModule } from "@/services/admin/modules.hooks";
import { addPackageModule, deletePackageModule, batchAddPackageModules, batchDeletePackageModules } from "@/services/admin/packages.service";
import ModuleCard from "./ModuleCard";
import { useDebounce } from "use-debounce";
import { useAppToast } from "@/hooks/useAppToast";
import { useQueryClient } from "@tanstack/react-query";
import { packageModuleQueryKeys } from "@/services/admin/modules.query-keys";

interface ModuleListProps {
  packageId: string;
}

const ModuleList: React.FC<ModuleListProps> = ({ packageId }) => {
  const [filterQuery, setFilterQuery] = useState("");
  const [debouncedFilterQuery] = useDebounce(filterQuery, 300);
  const [addAllDialogOpen, setAddAllDialogOpen] = useState(false);
  const [removeAllDialogOpen, setRemoveAllDialogOpen] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [isAddingAll, setIsAddingAll] = useState(false);
  const [processingModuleId, setProcessingModuleId] = useState<string | null>(null);

  // Fetch all modules and package modules using React Query
  const { data: allModules = [], isLoading: isLoadingModules } = useAllModules();
  const { data: packageModules = [], isLoading: isLoadingPackage } = usePackageModules(packageId);

  // Mutations for adding/removing modules
  const addMutation = useAddPackageModule();
  const removeMutation = useRemovePackageModule();
  const toast = useAppToast();
  const queryClient = useQueryClient();

  // Derive selected module IDs from package modules
  const selectedModuleIds = useMemo(() => {
    const ids = packageModules
      .map((pm: any) => {
        // PackageModuleDto has ModuleId field (capital M)
        // Try both camelCase and PascalCase
        const moduleId = pm.moduleId || pm.ModuleId;
        return moduleId;
      })
      .filter(Boolean);
    
    return new Set(ids);
  }, [packageModules]);

  // Filter modules based on search query
  const filteredModules = useMemo(() => {
    if (!debouncedFilterQuery.trim()) {
      return allModules;
    }

    const searchTerm = debouncedFilterQuery.toLowerCase();
    return allModules.filter((module) => {
      const name = (module.name || "").toLowerCase();
      const title = (module.title || "").toLowerCase();
      const description = (module.description || "").toLowerCase();
      const summarizedDescription = (module.summarizedDescription || "").toLowerCase();

      return (
        name.includes(searchTerm) ||
        title.includes(searchTerm) ||
        description.includes(searchTerm) ||
        summarizedDescription.includes(searchTerm)
      );
    });
  }, [allModules, debouncedFilterQuery]);

  // Separate modules into selected and unselected
  const { selectedModules, unselectedModules } = useMemo(() => {
    const selected: typeof allModules = [];
    const unselected: typeof allModules = [];

    filteredModules.forEach((module) => {
      if (selectedModuleIds.has(module.id)) {
        selected.push(module);
      } else {
        unselected.push(module);
      }
    });

    return { selectedModules: selected, unselectedModules: unselected };
  }, [filteredModules, selectedModuleIds]);

  // Handle toggle selection
  // Note: isSelected here is the NEW state (from onCheckedChange)
  const handleToggle = (moduleId: string, isSelected: boolean) => {
    if (processingModuleId) return; // Prevent multiple clicks
    
    setProcessingModuleId(moduleId);
    
    if (isSelected) {
      // Checkbox was checked: add module to package
      addMutation.mutate(
        { packageId, moduleId },
        {
          onSettled: () => {
            setProcessingModuleId(null);
          },
        }
      );
    } else {
      // Checkbox was unchecked: remove module from package
      removeMutation.mutate(
        { packageId, moduleId },
        {
          onSettled: () => {
            setProcessingModuleId(null);
          },
        }
      );
    }
  };

  // Handle add all unselected modules - opens confirmation dialog
  const handleAddAll = () => {
    if (unselectedModules.length === 0) return;
    setAddAllDialogOpen(true);
  };

  // Execute add all after confirmation
  const confirmAddAll = async () => {
    if (unselectedModules.length === 0) return;
    
    setAddAllDialogOpen(false);
    setIsAddingAll(true);
    
    try {
      // Execute batch add operation - wait for backend response
      const moduleIds = unselectedModules.map((module) => module.id);
      await batchAddPackageModules(packageId, moduleIds);
      
      toast.success(`${unselectedModules.length} módulo(s) adicionado(s) com sucesso`);
      
      // Invalidate to refetch after backend response
      queryClient.invalidateQueries({ 
        queryKey: packageModuleQueryKeys.list(packageId) 
      });
    } catch (error: any) {
      console.error("Error adding all modules:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Erro ao adicionar módulos";
      toast.error(errorMessage);
    } finally {
      setIsAddingAll(false);
    }
  };

  // Handle remove all selected modules - opens confirmation dialog
  const handleRemoveAll = () => {
    if (selectedModules.length === 0) return;
    setRemoveAllDialogOpen(true);
  };

  // Execute remove all after confirmation
  const confirmRemoveAll = async () => {
    if (selectedModules.length === 0) return;
    
    setRemoveAllDialogOpen(false);
    setIsRemovingAll(true);
    
    try {
      // Execute batch delete operation - wait for backend response
      const moduleIds = selectedModules.map((module) => module.id);
      await batchDeletePackageModules(packageId, moduleIds);
      
      toast.success(`${selectedModules.length} módulo(s) removido(s) com sucesso`);
      
      // Invalidate to refetch after backend response
      queryClient.invalidateQueries({ 
        queryKey: packageModuleQueryKeys.list(packageId) 
      });
    } catch (error: any) {
      console.error("Error removing all modules:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Erro ao remover módulos";
      toast.error(errorMessage);
    } finally {
      setIsRemovingAll(false);
    }
  };

  const isLoading = isLoadingModules || isLoadingPackage;

  return (
    <section className="mb-8">
      <div className="flex justify-between items-start mb-6 gap-4">
        <h2 className="text-xl font-bold text-neutral-900">Modules</h2>
        <div className="w-full sm:w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              type="text"
              placeholder="Buscar módulos..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="pl-10"
              disabled={isLoading}
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400 animate-spin" />
            )}
          </div>
          {debouncedFilterQuery && (
            <p className="text-sm text-neutral-600 mt-2">
              {filteredModules.length} módulo{filteredModules.length !== 1 ? 's' : ''} encontrado{filteredModules.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
          <p className="text-neutral-600">Carregando módulos...</p>
        </div>
      ) : filteredModules.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-neutral-600">
            {debouncedFilterQuery
              ? "Nenhum módulo encontrado com o filtro aplicado"
              : "Nenhum módulo disponível"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4">
          {/* Selected Modules - Left Column */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900">
                No Pacote
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-500 bg-neutral-100 px-2 py-1 rounded-full">
                  {selectedModules.length}
                </span>
                {selectedModules.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveAll}
                    disabled={isRemovingAll}
                  >
                    {isRemovingAll ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Removendo...
                      </>
                    ) : (
                      "Remover Todos"
                    )}
                  </Button>
                )}
              </div>
            </div>
            {selectedModules.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 text-sm">
                Nenhum módulo selecionado
              </div>
            ) : (
              selectedModules.map((module) => (
                <ModuleCard
                  key={module.id}
                  id={module.id}
                  title={module.title || module.name}
                  name={module.name}
                  description={module.summarizedDescription || module.description || "Sem descrição"}
                  isSelected={true}
                  onToggle={handleToggle}
                  isProcessing={processingModuleId === module.id}
                />
              ))
            )}
          </div>

          {/* Unselected Modules - Right Column */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900">
                Fora do Pacote
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-500 bg-neutral-100 px-2 py-1 rounded-full">
                  {unselectedModules.length}
                </span>
                {unselectedModules.length > 0 && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddAll}
                    disabled={isAddingAll}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isAddingAll ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Adicionando...
                      </>
                    ) : (
                      "Adicionar Todos"
                    )}
                  </Button>
                )}
              </div>
            </div>
            {unselectedModules.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 text-sm">
                Todos os módulos estão no pacote
              </div>
            ) : (
              unselectedModules.map((module) => (
                <ModuleCard
                  key={module.id}
                  id={module.id}
                  title={module.title || module.name}
                  name={module.name}
                  description={module.summarizedDescription || module.description || "Sem descrição"}
                  isSelected={false}
                  onToggle={handleToggle}
                  isProcessing={processingModuleId === module.id}
                />
              ))
            )}
          </div>
        </div>
      )}

      {(isRemovingAll || isAddingAll) && (
        <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-md shadow-lg">
          Atualizando...
        </div>
      )}

      {/* Add All Confirmation Dialog */}
      <Dialog open={addAllDialogOpen} onOpenChange={setAddAllDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Módulos ao Pacote</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                Você está prestes a adicionar <strong className="font-semibold text-neutral-900">{unselectedModules.length}</strong> módulo{unselectedModules.length !== 1 ? 's' : ''} ao pacote.
              </p>
              <p className="text-neutral-600">
                Os módulos serão incluídos imediatamente e estarão disponíveis para todos os estudantes que possuem este pacote.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddAllDialogOpen(false)}
              disabled={isAddingAll}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmAddAll}
              disabled={isAddingAll}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isAddingAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adicionando...
                </>
              ) : (
                "Adicionar Todos"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove All Confirmation Dialog */}
      <Dialog open={removeAllDialogOpen} onOpenChange={setRemoveAllDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover Módulos do Pacote</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                Você está prestes a remover <strong className="font-semibold text-neutral-900">{selectedModules.length}</strong> módulo{selectedModules.length !== 1 ? 's' : ''} do pacote.
              </p>
              <p className="text-neutral-600">
                Esta ação removerá o acesso a estes módulos para todos os estudantes que possuem este pacote. 
                Os módulos não serão excluídos do sistema, apenas removidos da associação com este pacote.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemoveAllDialogOpen(false)}
              disabled={isRemovingAll}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemoveAll}
              disabled={isRemovingAll}
            >
              {isRemovingAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Removendo...
                </>
              ) : (
                "Remover Todos"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ModuleList;
